import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { useSettings } from "../contexts/SettingsContext";

export default function Faq() {
  const { t, lang } = useI18n();
  const { settings } = useSettings();
  const items = (settings?.faq || []).filter((f) => (lang === "en" ? f.q_en : f.q_ro));
  const [openIdx, setOpenIdx] = useState(0);

  if (items.length === 0) return null;

  return (
    <section
      id="faq"
      className="py-24 md:py-32 px-6 md:px-12 max-w-[1100px] mx-auto"
      data-testid="faq-section"
    >
      <div className="text-center mb-14">
        <div className="label-eyebrow mb-4" style={{ color: "var(--brand-primary)" }}>
          {t("home.faqEyebrow")}
        </div>
        <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">
          {t("home.faqTitle")}
        </h2>
      </div>

      <div className="space-y-3" data-testid="faq-list">
        {items.map((it, i) => {
          const q = lang === "en" ? it.q_en : it.q_ro;
          const a = lang === "en" ? it.a_en : it.a_ro;
          const open = i === openIdx;
          return (
            <div
              key={i}
              className="card-surface overflow-hidden transition-all"
              data-testid={`faq-item-${i}`}
            >
              <button
                onClick={() => setOpenIdx(open ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                aria-expanded={open}
                data-testid={`faq-question-${i}`}
              >
                <span className="font-serif text-base sm:text-lg" style={{ color: "var(--brand-text-primary)" }}>
                  {q}
                </span>
                <ChevronDown
                  className="w-5 h-5 shrink-0 transition-transform"
                  style={{
                    color: "var(--brand-primary)",
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </button>
              <div
                className="grid transition-all duration-300 ease-out"
                style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
              >
                <div className="overflow-hidden">
                  <div
                    className="px-6 pb-5 text-sm leading-relaxed"
                    style={{ color: "var(--brand-text-secondary)" }}
                    data-testid={`faq-answer-${i}`}
                  >
                    {a}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* JSON-LD for SEO (FAQPage schema) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: items.map((it) => ({
              "@type": "Question",
              name: lang === "en" ? it.q_en : it.q_ro,
              acceptedAnswer: {
                "@type": "Answer",
                text: lang === "en" ? it.a_en : it.a_ro,
              },
            })),
          }),
        }}
      />
    </section>
  );
}
